import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ImageSource } from '../../types'
import styles from './Image.module.scss'
import { ImageCaption } from './ImageCaption'
import { ImageCredit } from './ImageCredit'

// Picture component prefered to be used instead (unless the ratio requirements are of no concern)
export const Image: FunctionComponent<ImageProps> = ({
  src,
  sources,
  alt = '',
  credit,
  caption,
  wrapperClassName,
  imageClassName,
  ...rest
}) => (
  <figure
    data-testid="image-wrapper"
    className={classnames(styles.imageElementWrapper, wrapperClassName)}
    {...rest}
  >
    <picture className={styles.imageElementPicture}>
      {sources &&
        sources.map(({ media, srcset }, index) => (
          <source
            key={`hero-image-source-${index + 1}`}
            media={media}
            srcSet={srcset}
          />
        ))}
      <img
        data-testid="image"
        className={classnames(styles.imageElementImage, imageClassName)}
        {...{ src, alt }}
        loading="lazy"
      />
    </picture>
    {credit && (
      <ImageCredit
        className={classnames(
          styles.imageElementCreditImage,
          caption
            ? styles.imageElementCreditWithCaption
            : styles.imageElementCredit
        )}
        {...{ credit }}
      />
    )}
    {caption && (
      <ImageCaption
        className={classnames(
          'sb-margin-top-1',
          'sb-margin-bottom-2',
          styles.imageElementCaption
        )}
        {...{ caption }}
      />
    )}
  </figure>
)

///////// IMPLEMENTATION /////////

export type ImageProps = {
  /**
   * Source link to image to be displayed. NB This image must be in jpeg or gif format.
   */
  src: string
  /**
   * Array of image sources that can be rendered for responsive breakpoints.
   */
  sources?: ImageSource[]
  /**
   * Alt text to be displayed for the image
   */
  alt?: string
  /**
   * Credit to external photographers if required, appears in bottom right corner of image
   */
  credit?: string
  /**
   * Optional caption to be displayed under the image
   */
  caption?: string
  /**
   * Class name that can be applied to the image outer wrapper for extra styling purposes
   */
  wrapperClassName?: string
  /**
   * Class name that can be applied specifically to the image for extra styling purposes
   */
  imageClassName?: string
} & ComponentPropsWithoutRef<'figure'>
